import { clientEnv } from '@/shared/config/env';
import { contentLocale } from '@/shared/i18n/messages';
import { requestJson } from '@/shared/request/http-client';

export type ActivityTierStatus = 'CLAIMABLE' | 'CLAIMED' | 'LOCKED';
export type ActivityMyStatus = 'CLAIMABLE' | 'CLAIMED' | 'EXPIRED' | 'IN_PROGRESS' | 'INELIGIBLE';

export interface ActivitySummary {
  activityId: string;
  bannerUrl: string | null;
  currency: string;
  /** 档位表单笔最高奖励（游客也返回，列表「最高可得」展示用） */
  maxRewardMinor: number | null;
  /** 当前可领槽位合计金额（仅登录态） */
  myClaimableAmountMinor: number | null;
  myClaimableCount: number | null;
  myMetricValue: number | null;
  /** 下一未达成档位门槛（仅登录态，全部领完为 null） */
  myNextThreshold: number | null;
  myStatus: ActivityMyStatus | null;
  sortOrder: number;
  subtitle: string | null;
  templateCode: string;
  /** 活动墙列表小方图（1:1），未配置时回退文字图标 */
  thumbUrl: string | null;
  title: string;
}

export interface ActivityRewardItem {
  couponPercentBps: number | null;
  itemType: string;
  qty: number;
}

export interface ActivityTier {
  grantKey: string;
  rewardMinor: number;
  rewards?: ActivityRewardItem[];
  status: ActivityTierStatus;
  threshold: number;
}

export type ActivityRescueLadderMode = 'LOSS_RANGE' | 'VIP_LEVEL';

export interface ActivityRescueLadderRow {
  minLossMinor: number | null;
  percentBps: number;
  rowKey: number;
  superPercentBps: number;
}

export interface ActivityRescueLadder {
  claimAfterHour: number;
  mode: ActivityRescueLadderMode;
  requireBetMinor: number | null;
  rows: ActivityRescueLadderRow[];
}

export interface ActivityDetail {
  activityId: string;
  bannerUrl: string | null;
  currency: string;
  /** 详情规则区长图；字段存在（含 null）时以服务端为准 */
  detailImageUrl?: string | null;
  /** MANUAL 显示领取按钮；IMMEDIATE/NEXT_DAY 自动发放，详情不出按钮 */
  grantTiming?: 'IMMEDIATE' | 'MANUAL' | 'NEXT_DAY' | string | null;
  /** 仅日救援金手动领取且后台打开扣积分时为 true */
  claimDeductPoints?: boolean | null;
  /** 领取需扣积分（最小单位，与可领金额同尺度）；开关关闭或无可领时为 0 */
  claimPointsCostMinor?: number | null;
  /** 日救援金第一档有奖净亏门槛（最小单位） */
  minClaimLossMinor?: number | null;
  myInviteCode: string | null;
  myMetricValue: number | null;
  myStatus: ActivityMyStatus | null;
  periodKey: string;
  rescueLadder?: ActivityRescueLadder | null;
  /** 仅 VIP_CHECKIN：服务端校验后的 reward_config_json */
  rewardConfigJson?: string | null;
  ruleText: string | null;
  subtitle: string | null;
  templateCode: string;
  tiers: ActivityTier[];
  title: string;
}

export interface ActivityClaimResult {
  balanceMinor: number;
  currency: string;
  duplicate: boolean;
  grantKey: string;
  grantNo: string;
  requiredTurnoverMinor: number;
  rewardMinor: number;
  rewards?: ActivityRewardItem[];
}

export interface ActivityPopupItem {
  gameExit?: { balanceBelowMinor: number; firstRewardMinor: number } | null;
  activityId: string;
  bannerUrl: string | null;
  currency: string;
  frequency: 'EVERY_ENTER' | 'ONCE' | 'ONCE_PER_SESSION' | 'UNTIL_CLAIMED' | string;
  maxRewardMinor: number | null;
  myClaimableAmountMinor: number | null;
  myClaimableCount: number | null;
  myStatus: ActivityMyStatus | null;
  priority: number;
  scene: string;
  sortOrder: number;
  subtitle: string | null;
  templateCode: string;
  title: string;
}

interface ClaimRequestBody {
  grantKey: string;
  siteId: string;
}

const query = () =>
  `siteId=${encodeURIComponent(clientEnv.siteId)}&locale=${encodeURIComponent(contentLocale())}`;

function sortOrderValue(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** 活动墙展示顺序：sortOrder 升序，相同则 activityId。不改入参数组。 */
export function sortActivitiesByOrder<T extends { activityId: string; sortOrder: number | null }>(
  items: T[],
): T[] {
  return [...items].sort((left, right) => {
    const orderDiff = sortOrderValue(left.sortOrder) - sortOrderValue(right.sortOrder);
    if (orderDiff !== 0) {
      return orderDiff;
    }
    return left.activityId.localeCompare(right.activityId);
  });
}

/** 活动墙列表：游客可访问，带 token 时返回个人进度。 */
export const listActivities = async (authToken?: string) => {
  const items = await requestJson<ActivitySummary[]>(`/v1/app/activity/list?${query()}`, {
    authToken: authToken || undefined,
    method: 'GET',
  });
  return sortActivitiesByOrder(items);
};

/** 弹窗候选：游客可访问；按 priority 降序。scene 默认 HOME。 */
export const listActivityPopups = (authToken?: string, scene = 'HOME', signal?: AbortSignal) =>
  requestJson<ActivityPopupItem[]>(
    `/v1/app/activity/popups?${query()}&scene=${encodeURIComponent(scene)}`,
    {
      signal,
      authToken: authToken || undefined,
      method: 'GET',
    },
  );

/** 活动详情：游客可访问，带 token 时档位含 CLAIMABLE/CLAIMED 状态。 */
export const getActivityDetail = (activityId: string, authToken?: string) =>
  requestJson<ActivityDetail>(
    `/v1/app/activity/detail/${encodeURIComponent(activityId)}?${query()}`,
    {
      authToken: authToken || undefined,
      method: 'GET',
    },
  );

export interface DepositPromoLadderTier {
  capMinor: number;
  maxDepositMinor: number;
  percentBps: number;
}

export interface DepositPromoFirstDeposit {
  activityId: string;
  cashMinor: number;
  claimed: boolean;
  eligible: boolean;
  ladder: DepositPromoLadderTier[];
  /** 首充积分加赠倍数；0 表示本次不送积分。 */
  pointsMultiplier: number;
  rewardMinor: number;
  title: string;
  totalMinor: number;
}

export interface DepositPromoRechargeBonus {
  activityId: string;
  cashMinor: number;
  eligible: boolean;
  ladder: DepositPromoLadderTier[];
  maxClaimsPerPeriod: number;
  remainingClaims: number;
  rewardMinor: number;
  title: string;
  totalMinor: number;
}

export interface DepositPromoCoupon {
  inventoryId: string;
  percentBps: number;
  rewardMinor: number;
  title: string;
}

export interface DepositPromo {
  coupons: DepositPromoCoupon[];
  currency: string;
  firstDeposit: DepositPromoFirstDeposit | null;
  /** 本次适用的积分万分比；0 表示不送。与入账共用。 */
  pointsMultiplierBps: number;
  rechargeBonus: DepositPromoRechargeBonus | null;
}

/** 充值页优惠预览：游客可访问，带 token 时叠加已充/已领资格。 */
export const getDepositPromo = (amountMinor: number, authToken?: string) => {
  const search = new URLSearchParams({
    siteId: clientEnv.siteId,
    locale: contentLocale(),
    amountMinor: String(Math.max(0, Math.trunc(amountMinor) || 0)),
  });
  return requestJson<DepositPromo>(`/v1/app/activity/deposit-promo?${search.toString()}`, {
    authToken: authToken || undefined,
    method: 'GET',
  });
};

/** 领取档位奖励：登录必需，服务端按 grant_key 幂等。 */
export const claimActivityReward = (activityId: string, grantKey: string, authToken: string) =>
  requestJson<ActivityClaimResult, ClaimRequestBody>(
    `/v1/app/activity/claim/${encodeURIComponent(activityId)}`,
    {
      authToken,
      body: { grantKey, siteId: clientEnv.siteId },
      method: 'POST',
    },
  );
